import React from 'react';
import { MdDownload } from 'react-icons/md';
import { useQuery } from '@tanstack/react-query';
import { runArtifactsBundleUrl, type ProcessArtifactDto } from '@api/processes';
import { listRunArtifacts } from '@api/processRuns';
import useAuthDownload from '@hooks/useAuthDownload';
import { DownloadAllButton } from './DownloadAllArtifactsButton.styled';

export interface DownloadAllArtifactsButtonProps {
  readonly nodeId: string;
  readonly artifacts: readonly ProcessArtifactDto[];
  readonly dataTestId: string;
}

const newestWithRun = (
  artifacts: readonly ProcessArtifactDto[],
): ProcessArtifactDto | undefined =>
  artifacts
    .filter((a) => a.runId)
    .reduce<ProcessArtifactDto | undefined>(
      (newest, a) =>
        !newest || Date.parse(a.createdAt) > Date.parse(newest.createdAt) ? a : newest,
      undefined,
    );

const RunBundleButton: React.FC<{
  readonly processId: string;
  readonly runId: string;
  readonly nodeId: string;
  readonly dataTestId: string;
}> = ({ processId, runId, nodeId, dataTestId }) => {
  const { download, downloading } = useAuthDownload();

  return (
    <DownloadAllButton
      onClick={(e) => {
        e.stopPropagation();
        download(
          runArtifactsBundleUrl(processId, runId, nodeId),
          `artifacts-run-${runId}-node-${nodeId}.zip`,
        ).catch(() => undefined);
      }}
      disabled={downloading}
      title="Download all of this node's files from the latest run as a ZIP"
      data-testid={dataTestId}
    >
      <MdDownload size={14} aria-hidden="true" />
      {downloading ? 'Preparing…' : 'Download all'}
    </DownloadAllButton>
  );
};

// Internal previews carry no port and aren't bundled.
const isBundled = (a: ProcessArtifactDto): boolean => !!a.portId;

// The editor snapshot keeps one artifact per port, so a single listed file can
// stand for a run that produced several; ask the run before hiding the button.
const useRunHasSeveralFiles = (
  latest: ProcessArtifactDto | undefined,
  nodeId: string,
  listedFromRun: number,
): boolean => {
  const runId = latest?.runId ?? undefined;
  const { data } = useQuery({
    queryKey: ['run-node-artifacts', latest?.processId, runId, nodeId, latest?.artifactId],
    queryFn: () => listRunArtifacts(latest!.processId, runId!, { nodeId }),
    enabled: !!runId && listedFromRun < 2,
    staleTime: Infinity,
    retry: false,
  });
  if (listedFromRun >= 2) return true;
  return (data?.filter(isBundled).length ?? 0) >= 2;
};

const DownloadAllArtifactsButton: React.FC<DownloadAllArtifactsButtonProps> = ({
  nodeId,
  artifacts,
  dataTestId,
}) => {
  const latest = newestWithRun(artifacts);
  const listedFromRun = latest
    ? artifacts.filter((a) => a.runId === latest.runId && isBundled(a)).length
    : 0;
  const hasSeveralFiles = useRunHasSeveralFiles(latest, nodeId, listedFromRun);
  if (!latest?.runId || !hasSeveralFiles) return null;
  return (
    <RunBundleButton
      processId={latest.processId}
      runId={latest.runId}
      nodeId={nodeId}
      dataTestId={dataTestId}
    />
  );
};

export default DownloadAllArtifactsButton;
